import { useEffect, type ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { AUTH_BYPASSED, apiScopes } from '../lib/auth/msal';
import { RoleProvider } from '../lib/roleContext';

/**
 * Wrap any internal-app route subtree with `<AuthGate>` to require a signed-in
 * Vari sales rep. When auth is bypassed (no Entra env vars yet), it's a no-op.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  if (AUTH_BYPASSED) return <RoleProvider>{children}</RoleProvider>;
  return <RealAuthGate>{children}</RealAuthGate>;
}

/** Only mounted when MSAL is configured; safe to call MSAL hooks here. */
function RealAuthGate({ children }: { children: ReactNode }) {
  const isAuth = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (isAuth) return;
    if (inProgress !== InteractionStatus.None) return; // login already in flight
    instance.loginRedirect({ scopes: apiScopes }).catch((err) => {
      console.error('[auth] loginRedirect failed', err);
    });
  }, [isAuth, inProgress, instance]);

  if (!isAuth) {
    return <SignInLoading />;
  }
  return <RoleProvider>{children}</RoleProvider>;
}

function SignInLoading() {
  return (
    <div className="h-full flex items-center justify-center bg-vari-beige/30">
      <div className="text-center space-y-3 max-w-sm px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-vari-blue-accessible">
          Vari · Idea Starter
        </p>
        <p className="text-2xl font-semibold text-vari-navy tracking-tight">
          Signing you in…
        </p>
        <p className="text-vari-gray text-sm">
          You're being redirected to your Vari Microsoft account. If nothing
          happens within a few seconds, refresh the page.
        </p>
      </div>
    </div>
  );
}
